import { env } from 'libs/constants/env';
import { Observable, OperatorFunction, from, of, switchMap } from 'rxjs';
import { ajax, AjaxConfig, AjaxResponse } from 'rxjs/ajax';
import RecaptchaProvider from 'domains/core-base/authentication/providers/recaptcha-provider';

export type BeforeInterceptors = (
  config: AjaxConfig,
) => Promise<AjaxConfig> | AjaxConfig | Observable<AjaxConfig>;
export type AfterInterceptors<T extends any = any> = (
  response: AjaxResponse<T>,
) => Observable<AjaxResponse<T>> | AjaxResponse<T> | Promise<AjaxResponse<T>>;

export class Interceptors {
  baseUrl = env.SERVER_URL;
  protected beforeInterceptors: BeforeInterceptors[] = [];
  protected afterInterceptors: AfterInterceptors[] = [];
  protected ajaxInstance;

  constructor() {
    this.ajaxInstance = ajax;
    this.beforeInterceptors = [
      this.addCustomHeadersBefore,
      this.addWrapperDataBefore,
    ];
    this.afterInterceptors = [this.getWrapperDataAfter];
  }

  addCustomHeadersBefore = (config: AjaxConfig) => {
    const { headers, url } = config;

    if (!url.includes(this.baseUrl)) return config;

    const newHeaders = {
      'Aplicacion-Id': 'ECAU',
      'Nombre-Aplicacion': 'Ecommerce Autos Clientes',
      'Usuario-Consumidor-Id': 'ANONIMO',
      'Nombre-Servicio-Consumidor': 'fe-ecommerceautos',
      'Ocp-apim-subscription-key': env.SUBSCRIPTION_KEY,
    };

    return Object.assign({
      ...config,
      headers: { ...headers, ...newHeaders },
    });
  };

  addWrapperDataBefore = (config: AjaxConfig) => {
    const { method, url, body } = config;

    if (method === 'GET' || !url.includes(this.baseUrl)) return config;

    const newConfig = {
      ...config,
      body: {
        datos: { ...body },
      },
    };

    return Object.assign({ ...config, ...newConfig });
  };

  validateRecaptchaBefore = async (config: AjaxConfig) => {
    const { url, headers } = config;

    if (!url.includes(this.baseUrl)) return config;

    const listUrl = [
      { url: 'customer', method: 'GET' },
      { url: 'customer', method: 'PATCH' },
      { url: '/vehicle', method: 'POST' },
      { url: '/niubiz/session-token', method: 'POST' },
      { url: '/niubiz/parameter', method: 'POST' },
      { url: '/niubiz/disengaged', method: 'POST' },
      { url: '/crm/opportunity', method: 'POST' },
      { url: 'quote', method: 'GET' },
      { url: 'quote', method: 'POST' },
      { url: '/external-web', method: 'POST' },
    ];

    const exist = listUrl.find(
      (item) => url.includes(item.url) && config.method === item.method,
    );

    if (!exist) return config;

    const recaptcha = RecaptchaProvider.getInstance();
    const loadKeyCaptcha = recaptcha.getLoadKeyCaptcha();
    loadKeyCaptcha && (await loadKeyCaptcha());

    return Object.assign({
      ...config,
      headers: { ...headers, 'g-recaptcha-response': recaptcha.getCaptcha() },
    });
  };

  getWrapperDataAfter = (result: AjaxResponse<any>) => {
    const { request, response } = result;
    const { url } = request;
    const { datos } = response ?? {};

    if (!url.includes(this.baseUrl)) return result;

    let newData;

    if (Array.isArray(datos)) {
      newData = [...datos];
    } else {
      newData = { ...datos };
    }

    return {
      ...result,
      response: newData,
    };
  };

  public executeRequest<T>(config: AjaxConfig): Observable<AjaxResponse<T>> {
    const beforeInterceptors: OperatorFunction<AjaxConfig, AjaxConfig>[] =
      this.beforeInterceptors.map((interceptor) =>
        switchMap((requestConfig: AjaxConfig) => {
          const result = interceptor(requestConfig);

          if (result instanceof Promise) {
            return from(result);
          }

          if (result instanceof Observable) {
            return result;
          }

          return of(result);
        }),
      );

    let execution: Observable<AjaxConfig> | Observable<AjaxResponse<T>> =
      of(config);

    for (const interceptor of beforeInterceptors) {
      execution = execution.pipe(interceptor);
    }

    execution = execution.pipe<AjaxResponse<T>>(
      switchMap((requestConfig) => this.ajaxInstance<T>(requestConfig)),
    );

    const afterInterceptors: OperatorFunction<
      AjaxResponse<T>,
      AjaxResponse<T>
    >[] = this.afterInterceptors.map((interceptor) =>
      switchMap((response: AjaxResponse<T>) => {
        const result = interceptor(response);

        if (result instanceof Promise) {
          return from(result);
        }

        if (result instanceof Observable) {
          return result;
        }

        return of(result);
      }),
    );

    for (const interceptor of afterInterceptors) {
      execution = execution.pipe(interceptor);
    }

    return execution;
  }
}
